import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs.toFixed(0)}s`;
}

/** Strip Font Awesome Private Use Area characters (U+F000-U+F8FF) that the backend embeds in model names. */
export function stripPua(s: string): string {
  return s.replace(/[\uF000-\uF8FF]/g, "").trimEnd();
}

/** Coerce an unknown value to a display string. Plain objects/arrays are
 * JSON-encoded so they don't render as "[object Object]"; null/undefined
 * return the fallback. */
export function toDisplayString(value: unknown, fallback = ""): string {
  if (value == null) return fallback;
  if (typeof value === "object") return JSON.stringify(value) ?? fallback;
  if (typeof value === "string") return value;
  if (typeof value === "symbol") return value.toString();
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  return fallback;
}

/** Return "#000" or "#fff" for best contrast against a hex background color. */
export function contrastText(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return r * 0.299 + g * 0.587 + b * 0.114 > 128 ? "#000" : "#fff";
}

export function base64ToBlob(base64: string, mimeType = "image/png"): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
}

const objectUrlCache = new Map<string, string>();

export function base64ToObjectUrl(base64: string, mimeType = "image/png"): string {
  const cached = objectUrlCache.get(base64);
  if (cached) return cached;
  const url = URL.createObjectURL(base64ToBlob(base64, mimeType));
  objectUrlCache.set(base64, url);
  return url;
}

/** Download a base64 image as a file */
export function downloadBase64Image(
  base64: string,
  filename: string,
  mimeType = "image/png",
): void {
  const blob = base64ToBlob(base64, mimeType);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Return a displayable URL for either a raw base64 string or an already-resolved URL. */
export function resolveImageSrc(image: string): string {
  if (
    image.startsWith("data:") ||
    image.startsWith("blob:") ||
    image.startsWith("/") ||
    image.startsWith("http")
  ) {
    return image;
  }
  return base64ToObjectUrl(image);
}

/** Download an image that may be either a URL or a raw base64 string. */
export async function downloadImage(image: string, filename: string): Promise<void> {
  if (image.startsWith("/") || image.startsWith("http")) {
    const response = await fetch(image);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } else {
    const raw = image.startsWith("data:") ? image.split(",")[1] : image;
    downloadBase64Image(raw, filename);
  }
}

/** Download all images from generation results as a zip file. */
export async function downloadAllAsZip(
  results: { images: string[]; info: string }[],
): Promise<void> {
  const JSZip = (await import("jszip")).default;
  const zip = new JSZip();
  let fileIndex = 0;
  for (const result of results) {
    for (let ii = 0; ii < result.images.length; ii++) {
      const image = result.images[ii];
      const filename = generateImageFilename(result.info, ii);
      let blob: Blob;
      if (image.startsWith("/") || image.startsWith("http")) {
        const resp = await fetch(image);
        blob = await resp.blob();
      } else {
        const raw = image.startsWith("data:") ? image.split(",")[1] : image;
        blob = base64ToBlob(raw);
      }
      zip.file(`${String(fileIndex).padStart(4, "0")}_${filename}`, blob);
      fileIndex++;
    }
  }
  const content = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(content);
  const a = document.createElement("a");
  a.href = url;
  a.download = `enso_${new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19)}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Generate a filename from generation info */
export function generateImageFilename(info: string, imageIndex: number): string {
  let seed = "unknown";
  let model = "image";
  try {
    const parsed = JSON.parse(info) as Record<string, unknown>;
    const parsedSeed = parsed["seed"];
    const parsedModel = parsed["model"];
    if (typeof parsedSeed === "string" || typeof parsedSeed === "number") seed = String(parsedSeed);
    if (typeof parsedModel === "string")
      model = parsedModel.split("/").pop()?.split(".")[0] ?? "image";
  } catch {
    /* fallback */
  }
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return `${model}_${seed}_${imageIndex}_${timestamp}.png`;
}
